import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { Alert, Box, Chip, Stack, Tab, Tabs, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useStore } from 'src/stores';
import { track } from 'src/configs/amplitude';
import { isLivableStaffFromToken } from 'src/utils/isLivableStaffFromToken';
import { CustomersView } from './CustomersView';
import { LoginsView } from './LoginsView';
import { OverviewView } from './OverviewView';
import { ProvidersView } from './ProvidersView';
import { SweepsView } from './SweepsView';
import { inbox, latestFullSweep, urgentCount } from './model';
import { useAsyncList } from './useAsyncList';
const VIEWS = [['overview', 'Overview'], ['sweeps', 'Sweeps'], ['providers', 'Providers'], ['customers', 'Customers'], ['logins', 'Logins']];

/** The API's own words when it gave any (a 503 saying this environment cannot read it), else `fallback`. */
const apiError = fallback => model => {
  var _model$error, _errorResp$body;
  const errorResp = model === null || model === void 0 || (_model$error = model.error) === null || _model$error === void 0 ? void 0 : _model$error.errorResp;
  const detail = [errorResp === null || errorResp === void 0 ? void 0 : errorResp.title, ...((_errorResp$body = errorResp === null || errorResp === void 0 ? void 0 : errorResp.body) !== null && _errorResp$body !== void 0 ? _errorResp$body : [])].flat(Infinity).filter(part => typeof part === 'string' && part.trim() && part !== 'Error').join(' ');
  return detail || fallback;
};
const Console = observer(_ref => {
  let embedded = _ref.embedded;
  const store = useStore();
  const navigate = useNavigate();
  const _useSearchParams = useSearchParams(),
    _useSearchParams2 = _slicedToArray(_useSearchParams, 2),
    params = _useSearchParams2[0],
    setParams = _useSearchParams2[1];
  const view = VIEWS.some(_ref2 => {
    let _ref3 = _slicedToArray(_ref2, 1),
      key = _ref3[0];
    return key === params.get('view');
  }) ? params.get('view') : 'overview';
  const filter = params.get('filter') || '';
  const health = useAsyncList(store.admin.getBillScrapingHealthAsync, () => store.admin.getBillScrapingHealthAsync.run(30), 'Could not load scraper health.', [store]);
  const scoreboard = useAsyncList(store.admin.getBillScrapingScoreboardAsync, () => store.admin.getBillScrapingScoreboardAsync.run(90), apiError('Could not load the scoreboard.'), [store]);
  // Every company, not the header's selected one: support searches by customer here.
  const credentials = useAsyncList(store.admin.getUtilityCredentialsAsync, () => store.admin.getUtilityCredentialsAsync.run(undefined), apiError('Could not load utility credentials.'), [store]);
  useEffect(() => {
    track('Bill Automation Console Viewed', {
      view
    });
  }, [view]);
  const urgent = urgentCount(inbox({
    credentials: credentials.rows,
    sweep: latestFullSweep(health.rows),
    scoreboard: scoreboard.rows[0]
  }));
  const go = (next, nextFilter) => {
    const search = new URLSearchParams(params);
    search.set('view', next);
    if (nextFilter) search.set('filter', nextFilter);else search.delete('filter');
    setParams(search);
  };
  const handleNavigate = item => {
    if (item.kind) track('Bill Automation Inbox Item Clicked', {
      kind: item.kind
    });
    if (item.href) navigate(item.href);else go(item.view, item.filter);
  };
  return (
    /*#__PURE__*/
    // On its own route there is no Admin page Container around it, so it brings its own gutter.
    React.createElement(Box, {
      sx: {
        marginTop: '16px',
        paddingX: embedded ? 0 : {
          xs: '16px',
          md: '32px'
        },
        paddingBottom: '32px'
      }
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "h4"
    }, "Bill automation"), /*#__PURE__*/React.createElement(Tabs, {
      "aria-label": "bill automation views",
      value: view,
      onChange: (_, next) => next && go(next),
      variant: "scrollable",
      allowScrollButtonsMobile: true
    }, VIEWS.map(_ref4 => {
      let _ref5 = _slicedToArray(_ref4, 2),
        key = _ref5[0],
        label = _ref5[1];
      return /*#__PURE__*/React.createElement(Tab, {
        key: key,
        value: key,
        label:
        // Inline, not a Badge: a badge pushed outside the tab is clipped by the tab's own overflow.
        key === 'overview' && urgent ? /*#__PURE__*/React.createElement(Stack, {
          direction: "row",
          spacing: 6,
          alignItems: "center"
        }, /*#__PURE__*/React.createElement("span", null, label), /*#__PURE__*/React.createElement(Chip, {
          size: "small",
          color: "error",
          label: urgent > 999 ? '999+' : urgent,
          "aria-label": "".concat(urgent, " need attention"),
          sx: {
            height: 20,
            fontWeight: 700
          }
        })) : label
      });
    })), view === 'overview' ? /*#__PURE__*/React.createElement(OverviewView, {
      health: health,
      scoreboard: scoreboard,
      credentials: credentials,
      onNavigate: handleNavigate
    }) : null, view === 'sweeps' ? /*#__PURE__*/React.createElement(SweepsView, {
      health: health
    }) : null, view === 'providers' ? /*#__PURE__*/React.createElement(ProvidersView, {
      key: filter,
      health: health,
      scoreboard: scoreboard,
      credentials: credentials,
      filter: filter,
      onNavigate: handleNavigate
    }) : null, view === 'customers' ? /*#__PURE__*/React.createElement(CustomersView, null) : null, view === 'logins' ? /*#__PURE__*/React.createElement(LoginsView, {
      key: filter,
      credentials: credentials,
      filter: filter
    }) : null)
  );
});

/**
 * Bill automation console: how much of the team's manual month the scraper now covers, and what needs a person.
 *
 * Mounted at /bill-automation and as an Admin tab. Staff only: the gate below is cosmetic, every endpoint behind
 * it is [Authorize(AdminOrHigher)] and cross-company. The three loads here are shared by every view, because
 * `run()` on one async model cancels a concurrent call, so two views must never fetch the same list.
 */
export const BillAutomationPage = _ref6 => {
  let _ref6$embedded = _ref6.embedded,
    embedded = _ref6$embedded === void 0 ? false : _ref6$embedded;
  return isLivableStaffFromToken() ? /*#__PURE__*/React.createElement(Console, {
    embedded: embedded
  }) : /*#__PURE__*/React.createElement(Alert, {
    severity: "info"
  }, "This page is for Livable staff.");
};